// PanoramaViewer — a small window that floats ABOVE the tapped intersection node
// on the map (PathfinderTab portals it into a map-anchored div). The user turns
// the photo to look around; as they turn, `onView` reports the compass bearing
// the centre of the view faces so the map draws a direction cone at the node.
// The only overlay is a "GO" arrow pointing up at the direction of the next step.
//
// The photo is a `PanoStrip`: a 360 panorama rotates continuously, stitched at
// the seam, and keeps its momentum when you let go — turning on the spot rather
// than scrubbing a strip that runs out. A flat photo doesn't wrap, since its
// edges are edges.
//
// Because the window lives inside the map's overlay panes, Google Maps' own drag
// listeners would otherwise pan the map when you drag the photo. React's
// stopPropagation can't stop those (they're native DOM listeners), so we attach
// native listeners on the window that stopPropagation for the map-gesture events.
import { useEffect, useRef, useCallback } from 'react';
import PanoStrip from './PanoStrip.jsx';

const norm180 = (deg) => ((deg % 360) + 540) % 360 - 180; // → (−180, 180]
const norm360 = (deg) => ((deg % 360) + 360) % 360;
const wrap01 = (f) => ((f % 1) + 1) % 1;

export default function PanoramaViewer({ imageUrl, kind = 'flat', heading = 0, targetBearing = 0, fov, exitFrac: exitFracProp, onView, onClose }) {
  const isSphere = kind === 'equirect';
  const fovEff = isSphere ? 360 : (fov || 180);
  const rel = norm180(targetBearing - heading);
  // The exit's spot in the photo: an exact per-edge fraction when supplied,
  // else derived from the target bearing vs. the fitted heading/fov.
  const hasExact = typeof exitFracProp === 'number';
  const exitFrac = hasExact ? (isSphere ? wrap01(exitFracProp) : Math.max(0, Math.min(1, exitFracProp)))
    : isSphere
      ? wrap01(0.5 + rel / 360)
      : Math.max(0, Math.min(1, 0.5 + rel / fovEff));
  // A sphere can always be turned to face the exit, so the arrow is always
  // reachable; a flat photo may simply not contain that direction.
  const inView = isSphere || hasExact || Math.abs(rel) <= Math.abs(fovEff) / 2;

  const winRef = useRef(null);
  const goRef = useRef(null);
  const stripRef = useRef(null);
  const metricsRef = useRef({ imgW: 0, hostW: 0 });
  const onViewRef = useRef(onView);
  useEffect(() => { onViewRef.current = onView; }, [onView]);

  // Report the bearing the centre faces + the visible slice, and put the GO
  // arrow at the exit — on a wrapping strip, at whichever repeat of the image is
  // nearest the current view, so it slides in from the correct side.
  const place = useCallback((centreFrac) => {
    const { imgW, hostW } = metricsRef.current;
    onViewRef.current?.(norm360(heading + (centreFrac - 0.5) * fovEff),
      Math.abs(imgW > 0 ? (hostW / imgW) * fovEff : fovEff));
    const el = goRef.current;
    if (!el || !imgW) return;
    let d = exitFrac - centreFrac;
    if (isSphere) d = norm180(d * 360) / 360;   // nearest repeat, not the literal one
    const x = hostW / 2 + d * imgW;
    el.style.left = `${x}px`;
    el.style.visibility = x < -30 || x > hostW + 30 ? 'hidden' : 'visible';
  }, [heading, fovEff, exitFrac, isSphere]);

  // If the step being shown changes while the window is open, turn to the new
  // exit. `initialFrac` covers the first show, so skip that one or this would
  // fight it before the image has even sized.
  const firstRef = useRef(true);
  useEffect(() => { firstRef.current = true; }, [imageUrl]);
  useEffect(() => {
    if (firstRef.current) { firstRef.current = false; return; }
    stripRef.current?.setCentreFrac(exitFrac);
  }, [exitFrac]);

  // Keep map-gesture events from reaching the map underneath.
  useEffect(() => {
    const win = winRef.current;
    if (!win) return;
    const stop = (e) => e.stopPropagation();
    const events = ['pointerdown', 'mousedown', 'touchstart', 'touchmove', 'wheel', 'dblclick', 'contextmenu'];
    events.forEach(ev => win.addEventListener(ev, stop));
    return () => events.forEach(ev => win.removeEventListener(ev, stop));
  }, []);

  return (
    <div className="pfa-pano-win" ref={winRef}>
      <button className="pfa-pano-x" onClick={onClose} aria-label="Close">✕</button>
      {imageUrl ? (
        <PanoStrip
          ref={stripRef}
          className="pfa-pano-strip"
          hostClassName="pfa-pano-scroll"
          imgClassName="pfa-pano-img"
          src={imageUrl}
          alt="Intersection panorama"
          wrap={isSphere}
          momentum
          initialFrac={exitFrac}
          onMetrics={(m) => { metricsRef.current = m; }}
          onFrac={place}
        >
          {inView && (
            <div className="pfa-pano-go" ref={goRef} aria-hidden="true">
              <svg viewBox="0 0 34 40" width="30" height="36"><path d="M17 3 L30 22 L21 22 L21 37 L13 37 L13 22 L4 22 Z" fill="#39ff14" stroke="#0a2e0a" strokeWidth="2.5" strokeLinejoin="round"/></svg>
            </div>
          )}
        </PanoStrip>
      ) : <div className="pfa-pano-missing">No panorama.</div>}
      <span className="pfa-pano-tail" aria-hidden="true" />
    </div>
  );
}
